import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, Chip, IconButton, Rating, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";

const SENTIMENT_LABELS = {
  1: "Highly Unsatisfied",
  2: "Unsatisfied",
  3: "Neutral",
  4: "Satisfied",
  5: "Highly Satisfied",
};

const SENTIMENT_COLORS = {
  1: "#EF5350",
  2: "#FF7043",
  3: "#FFA726",
  4: "#66BB6A",
  5: "#2E7D32",
};

export default function Feedback({ open, setOpen }) {
  const [value, setValue] = React.useState(0);
  const [hover, setHover] = React.useState(-1);
  const [feedbackText, setFeedbackText] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);

  const loginData = JSON.parse(localStorage.getItem("loginDetails"));

  const handleSubmit = () => {
    const feedbackObj = {
      clientId: loginData?.clientId ?? "",
      center: loginData?.center ?? "",
      org: loginData?.org ?? "",
      device: navigator.userAgentData
        ? navigator.userAgentData.mobile
          ? "Mobile"
          : "Desktop"
        : "Apple Mobile",
      devicePlatform: navigator.userAgentData
        ? navigator.userAgentData.platform
        : "IOS/Android",
      browser: navigator.userAgentData
        ? navigator.userAgentData.brands[navigator.userAgentData.brands.length - 1]?.brand ?? ""
        : "Safari/Firefox/WebView Android",
      referralScore: value,
      comments: feedbackText,
    };
    axios
      .post(
        "https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/feedback/clientdashboard",
        feedbackObj
      )
      .then((response) => {
        if (response.status === 200) setSubmitted(true);
      })
      .catch((error) => {
        console.error("Error submitting Feedback:", error);
      });
  };

  const handleClose = () => {
    setValue(0);
    setHover(-1);
    setFeedbackText("");
    setSubmitted(false);
    setOpen(false);
  };

  const activeStar = hover !== -1 ? hover : value;

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: { sm: "380px", xs: "320px" },
            overflow: "hidden",
            boxShadow: "0px 8px 32px rgba(53, 103, 214, 0.18)",
          },
        }}
      >
        {/* Gradient header with illustration */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #3567D6 0%, #5A8FF5 100%)",
            pt: 2.5,
            pb: 3,
            px: 3,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close feedback dialog"
            sx={{
              position: "absolute",
              top: 10,
              right: 12,
              color: "rgba(255,255,255,0.85)",
              "&:hover": { color: "#fff", background: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.75)",
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}
          >
            Quick Feedback
          </Typography>
          <Box
            sx={{
              width: 90,
              height: 90,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="https://niroggyan.s3.ap-south-1.amazonaws.com/feedback/vizApp-feedback-image.png"
              alt="feedback illustration"
              width={64}
              height={64}
              style={{ objectFit: "contain" }}
            />
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 2 }}>
          {/* Rating + optional comment — shown before submit */}
          {!submitted && (
            <Stack alignItems="center" gap={2}>
              <Typography
                sx={{
                  color: "#1A1A2E",
                  fontSize: "17px",
                  fontWeight: 600,
                  textAlign: "center",
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  lineHeight: 1.4,
                }}
              >
                Rate your experience with this app
              </Typography>

              {/* Dynamic sentiment chip */}
              <Box sx={{ minHeight: 28 }}>
                {activeStar > 0 && (
                  <Chip
                    label={SENTIMENT_LABELS[activeStar]}
                    size="small"
                    sx={{
                      background: `${SENTIMENT_COLORS[activeStar]}18`,
                      color: SENTIMENT_COLORS[activeStar],
                      fontWeight: 600,
                      fontSize: "12px",
                      border: `1px solid ${SENTIMENT_COLORS[activeStar]}40`,
                      fontFamily: "'IBM Plex Sans', sans-serif",
                    }}
                  />
                )}
              </Box>

              <Rating
                name="feedback-rating"
                value={value}
                onChange={(_, newValue) => setValue(newValue)}
                onChangeActive={(_, newHover) => setHover(newHover)}
                emptyIcon={<StarIcon style={{ opacity: 0.25, fontSize: "38px" }} />}
                icon={<StarIcon style={{ fontSize: "38px" }} />}
                sx={{
                  gap: "4px",
                  "& .MuiRating-iconFilled": { color: "#F9C852" },
                  "& .MuiRating-iconHover": { color: "#FFB300", transform: "scale(1.18)" },
                  "& .MuiRating-icon": { transition: "transform 0.15s ease" },
                }}
              />

              {/* Comment + submit — shown after a star is selected */}
              {value !== 0 && (
                <>
                  <Box sx={{ width: "100%", height: "1px", background: "#EBEBEB", my: 0.5 }} />
                  <Typography
                    sx={{
                      color: "#1A1A2E",
                      fontSize: "14px",
                      fontWeight: 600,
                      alignSelf: "flex-start",
                      fontFamily: "'IBM Plex Sans', sans-serif",
                    }}
                  >
                    Add a comment{" "}
                    <Typography
                      component="span"
                      sx={{ color: "#AEAEAE", fontWeight: 400, fontSize: "13px" }}
                    >
                      (optional)
                    </Typography>
                  </Typography>
                  <TextField
                    id="feedback_text"
                    placeholder="Tell us what we can improve..."
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        fontSize: "13px",
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        "& fieldset": { borderColor: "#DEDEDE" },
                        "&:hover fieldset": { borderColor: "#3567D6" },
                        "&.Mui-focused fieldset": { borderColor: "#3567D6" },
                      },
                      "& .MuiInputBase-input": { color: "#333" },
                    }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{
                      background: "linear-gradient(135deg, #3567D6 0%, #5A8FF5 100%)",
                      color: "#fff",
                      borderRadius: "10px",
                      py: 1.2,
                      fontSize: "14px",
                      fontWeight: 600,
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      textTransform: "none",
                      boxShadow: "0px 4px 14px rgba(53, 103, 214, 0.35)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #2855be 0%, #4a7ae0 100%)",
                        boxShadow: "0px 6px 18px rgba(53, 103, 214, 0.45)",
                      },
                    }}
                  >
                    Submit Feedback
                  </Button>
                </>
              )}
            </Stack>
          )}

          {/* Thank-you confirmation state */}
          {submitted && (
            <Stack alignItems="center" gap={1.5} py={1}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #3567D6 0%, #5A8FF5 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <CheckIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography
                sx={{
                  color: "#1A1A2E",
                  fontSize: "18px",
                  fontWeight: 700,
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  textAlign: "center",
                }}
              >
                Thanks for your Feedback!
              </Typography>
              <Typography
                sx={{
                  color: "#7F7F7F",
                  fontSize: "13px",
                  fontWeight: 400,
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  textAlign: "center",
                  lineHeight: 1.6,
                }}
              >
                We appreciate your time. Your feedback helps us build a better experience.
              </Typography>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  mt: 1,
                  borderRadius: "10px",
                  borderColor: "#3567D6",
                  color: "#3567D6",
                  textTransform: "none",
                  fontWeight: 600,
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  "&:hover": { background: "#f5f9ff", borderColor: "#3567D6" },
                }}
              >
                Close
              </Button>
            </Stack>
          )}
        </DialogContent>

        {/* Footer note */}
        <Typography
          sx={{
            textAlign: "center",
            color: "#AEAEAE",
            fontSize: "10px",
            fontWeight: 400,
            fontFamily: "'IBM Plex Sans', sans-serif",
            pb: 1.5,
            px: 2,
          }}
        >
          Your rating makes our app even better to serve you
        </Typography>
      </Dialog>
    </React.Fragment>
  );
}
